import { useCallback, useEffect, useRef, useState } from "react";
import type { ICameraVideoTrack } from "agora-rtc-react";
import type { IVirtualBackgroundProcessor } from "agora-extension-virtual-background";
import {
  isVirtualBackgroundSupported,
  virtualBackgroundExtension,
} from "../agoraVirtualBackground";

const DEFAULT_BLUR_DEGREE = 2;

export const useVirtualBackgroundBlur = (
  localCameraTrack: ICameraVideoTrack | null,
) => {
  const [blurEnabled, setBlurEnabled] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const isSupported = useRef(isVirtualBackgroundSupported()).current;
  const processorRef = useRef<IVirtualBackgroundProcessor | null>(null);
  const trackRef = useRef<ICameraVideoTrack | null>(null);
  const pipedRef = useRef(false);
  const applyingRef = useRef(false);

  const applyBlurState = useCallback(async (enabled: boolean) => {
    const processor = processorRef.current;
    const track = trackRef.current;

    if (!processor || !track || applyingRef.current) {
      return;
    }

    applyingRef.current = true;

    try {
      if (enabled) {
        processor.setOptions({ type: "blur", blurDegree: DEFAULT_BLUR_DEGREE });

        if (!pipedRef.current) {
          track.pipe(processor).pipe(track.processorDestination);
          pipedRef.current = true;
        }

        await processor.enable();
        return;
      }

      await processor.disable();

      if (pipedRef.current) {
        track.unpipe();
        pipedRef.current = false;
      }
    } catch (error) {
      console.error("Falha ao alternar desfoque:", error);
    } finally {
      applyingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isSupported || !localCameraTrack) {
      return;
    }

    let active = true;
    trackRef.current = localCameraTrack;
    pipedRef.current = false;

    const setup = async () => {
      try {
        const processor = virtualBackgroundExtension.createProcessor();
        await processor.init();

        if (!active) {
          await processor.release();
          return;
        }

        processorRef.current = processor;
        setIsReady(true);
      } catch (error) {
        console.error("Falha ao iniciar fundo desfocado:", error);
        processorRef.current = null;
        setIsReady(false);
      }
    };

    setup();

    return () => {
      active = false;

      const processor = processorRef.current;
      const track = trackRef.current;

      if (track && pipedRef.current) {
        track.unpipe();
        pipedRef.current = false;
      }

      if (processor) {
        processor.disable();
        processor.release();
      }

      processorRef.current = null;
      trackRef.current = null;
      setIsReady(false);
    };
  }, [isSupported, localCameraTrack]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    applyBlurState(blurEnabled);
  }, [blurEnabled, isReady, applyBlurState]);

  const toggleBlur = useCallback(() => {
    setBlurEnabled((current) => !current);
  }, []);

  return {
    blurEnabled,
    toggleBlur,
    isSupported,
    isReady,
  };
};
